import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { EVENT_SPECIALIZATIONS } from "../data/specializations";
import { ArrowLeft, Plus, Link2, Info, Trash2 } from "lucide-react";

export default function AddRole() {
  const { id } = useParams(); // event id
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("new"); // "new" or "link"
  const [myJobs, setMyJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [updateJobDate, setUpdateJobDate] = useState(null); // null = not asked yet
  const [form, setForm] = useState({
    title: "", specialization: "", description: "", payment: "", total_slots: 1, customSpec: "", showCustom: false,
  });
  const [loading, setLoading] = useState(false);

  // Fines
  const [finesEnabled, setFinesEnabled] = useState(false);
  const [useCustomFines, setUseCustomFines] = useState(false);
  const [fineCategories, setFineCategories] = useState([]);
  const [fineNotes, setFineNotes] = useState("");
  const [fineIntroOpen, setFineIntroOpen] = useState(false);
  const hasManagerDefaults = user?.fine_categories?.length > 0;
  const addFineCategory = () => setFineCategories(c => [...c, { name: "", rate_per_star: "" }]);
  const updateFineCategory = (i, field, val) => setFineCategories(c => c.map((cat, idx) => idx === i ? { ...cat, [field]: val } : cat));
  const removeFineCategory = (i) => setFineCategories(c => c.filter((_, idx) => idx !== i));

  // Travel allowance
  const [travelExpenseEnabled, setTravelExpenseEnabled] = useState(false);
  const [useCustomTravelExpense, setUseCustomTravelExpense] = useState(false);
  const [travelExpenseRate, setTravelExpenseRate] = useState("");
  const [travelExpenseMinKm, setTravelExpenseMinKm] = useState("");
  const [travelExpenseMaxAmount, setTravelExpenseMaxAmount] = useState("");
  const hasTravelDefault = (user?.travel_expense_rate || 0) > 0;
  const hasOfficeLocation = user?.lat != null && user?.lng != null;

  useEffect(() => {
    api.get(`/events/${id}`).then(r => setEvent(r.data)).catch(() => {});
    api.get("/me/jobs").then(r => {
      // Only show active, unlinked event-staffing jobs — exclude closed/completed/cancelled/draft
      setMyJobs(r.data.filter(j =>
        !j.event_id &&
        j.opportunity_type === "event_staffing" &&
        ["active", "open"].includes(j.status)
      ));
    }).catch(() => {});
  }, [id]);

  const submitNew = async (e) => {
    e.preventDefault();
    const spec = form.showCustom ? form.customSpec.trim() : form.specialization;
    if (!spec) { toast.error("Please select a category"); return; }
    if (!form.payment) { toast.error("Payment is required"); return; }
    if (finesEnabled && useCustomFines && !fineNotes.trim()) {
      toast.error("Fine policy notes are required when custom fines are enabled — workers need to know why they're being fined.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/jobs", {
        opportunity_type: "event_staffing",
        title: form.title,
        specialization: spec,
        description: form.description,
        payment: parseFloat(form.payment),
        total_slots: parseInt(form.total_slots) || 1,
        event_id: id,
        // Inherit from event
        location: event?.location,
        lat: event?.lat,
        lng: event?.lng,
        event_date: event?.date,
        fines_enabled: finesEnabled,
        use_custom_fines: useCustomFines,
        fine_categories: useCustomFines ? fineCategories.filter(c => c.name.trim()).map(c => ({ name: c.name.trim(), rate_per_star: Math.max(0, parseFloat(c.rate_per_star) || 0) })) : null,
        fine_notes: useCustomFines ? fineNotes.trim() : null,
        travel_expense_enabled: travelExpenseEnabled,
        use_custom_travel_expense: useCustomTravelExpense,
        travel_expense_rate: useCustomTravelExpense ? Math.max(0, parseFloat(travelExpenseRate) || 0) : null,
        travel_expense_min_km: useCustomTravelExpense ? Math.max(0, parseFloat(travelExpenseMinKm) || 0) : null,
        travel_expense_max_amount: useCustomTravelExpense ? Math.max(0, parseFloat(travelExpenseMaxAmount) || 0) : null,
      });
      toast.success("Role added to event!");
      nav(`/manage-event/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add role");
    } finally { setLoading(false); }
  };

  const linkJob = async () => {
    if (!selectedJob) { toast.error("Select a job to link"); return; }
    setLoading(true);
    try {
      // Link the job to this event
      await api.patch(`/jobs/${selectedJob.id}`, {
        event_id: id,
        // Update date/venue if manager chose yes
        ...(updateJobDate === true ? { event_date: event?.date, location: event?.location, lat: event?.lat, lng: event?.lng } : {}),
      });
      toast.success("Job linked to event!");
      nav(`/manage-event/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link job");
    } finally { setLoading(false); }
  };

  if (!event) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8">
      <Link to={`/manage-event/${id}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to {event.name}
      </Link>

      <span className="label-eyebrow text-muted-foreground">Add role</span>
      <h1 className="mt-1 font-display text-3xl tracking-tighter">{event.name}</h1>
      <p className="text-sm text-muted-foreground mt-1">
        {event.date && new Date(event.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
        {event.venue && ` · ${event.venue}`}
      </p>

      {/* Tab toggle */}
      <div className="mt-6 flex gap-2">
        <button onClick={() => setTab("new")} className={`chip ${tab === "new" ? "chip-active" : ""}`}>
          <Plus className="w-3.5 h-3.5" /> Create new role
        </button>
        <button onClick={() => setTab("link")} className={`chip ${tab === "link" ? "chip-active" : ""}`}>
          <Link2 className="w-3.5 h-3.5" /> Link existing job
        </button>
      </div>

      {/* Create new role */}
      {tab === "new" && (
        <form onSubmit={submitNew} className="mt-6 space-y-4">
          <div>
            <label className="label-eyebrow text-muted-foreground">Role title <span className="text-red-500">*</span></label>
            <input className="input-base mt-2" required placeholder="e.g. Head Catering Captain"
              value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          </div>

          <div>
            <label className="label-eyebrow text-muted-foreground">Category <span className="text-red-500">*</span></label>
            {!form.showCustom ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {EVENT_SPECIALIZATIONS.map(s => (
                  <button key={s} type="button" onClick={() => setForm({...form, specialization: s, showCustom: false})}
                    className={`chip ${form.specialization === s ? "chip-active" : ""}`}>{s}</button>
                ))}
                <button type="button" onClick={() => setForm({...form, showCustom: true, specialization: ""})}
                  className="chip border-dashed border-brand text-brand">+ Custom</button>
              </div>
            ) : (
              <div className="flex gap-2 mt-2">
                <input className="input-base flex-1" placeholder="Enter custom category…" autoFocus
                  value={form.customSpec} onChange={e => setForm({...form, customSpec: e.target.value})} />
                <button type="button" onClick={() => setForm({...form, showCustom: false})} className="btn-outline">Back</button>
              </div>
            )}
          </div>

          <div>
            <label className="label-eyebrow text-muted-foreground">Description</label>
            <textarea rows={3} className="input-base mt-2" placeholder="Requirements, dress code, duties…"
              value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow text-muted-foreground">Payment per person (₹) <span className="text-red-500">*</span></label>
              <input type="number" min={0} className="input-base mt-2" required placeholder="500"
                value={form.payment} onChange={e => setForm({...form, payment: e.target.value})} />
            </div>
            <div>
              <label className="label-eyebrow text-muted-foreground">Number of slots</label>
              <input type="number" min={1} className="input-base mt-2"
                value={form.total_slots} onChange={e => setForm({...form, total_slots: e.target.value})} />
            </div>
          </div>

          <div className="border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
                <input type="checkbox" checked={finesEnabled} onChange={e => setFinesEnabled(e.target.checked)} className="w-4 h-4" />
                Enable rating fines for this job
              </label>
              <button type="button" onClick={() => setFineIntroOpen(true)} className="text-xs text-brand hover:underline flex items-center gap-1">
                <Info className="w-3.5 h-3.5" /> What's this?
              </button>
            </div>

            {finesEnabled && (
              <div className="mt-3 space-y-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={useCustomFines} onChange={e => setUseCustomFines(e.target.checked)} className="w-4 h-4" />
                  Use custom fines for this job
                </label>

                {!useCustomFines && (
                  <p className="text-xs text-muted-foreground">
                    {hasManagerDefaults
                      ? "Your default rating categories (set in your profile) will apply to this job."
                      : "You haven't set default rating categories yet. Set them in your profile, or check \"Use custom fines for this job\" to define them here."}
                  </p>
                )}

                {useCustomFines && (
                  <>
                    <div>
                      <label className="label-eyebrow text-muted-foreground">Star categories</label>
                      <div className="mt-2 space-y-2">
                        {fineCategories.map((cat, i) => (
                          <div key={i} className="flex gap-2 items-center">
                            <input className="input-base !py-2 text-sm min-w-0" style={{ flex: '2 1 0%' }} placeholder="e.g. Uniform"
                              value={cat.name} onChange={e => updateFineCategory(i, "name", e.target.value)} />
                            <span className="text-xs text-muted-foreground">₹</span>
                            <input type="number" min="0" className="input-base !py-2 text-sm" style={{ flex: '0 0 5rem' }} placeholder="per star"
                              value={cat.rate_per_star} onChange={e => updateFineCategory(i, "rate_per_star", e.target.value)} />
                            <button type="button" onClick={() => removeFineCategory(i)} className="text-muted-foreground hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={addFineCategory} className="mt-2 text-xs text-brand hover:underline flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Add category
                      </button>
                    </div>

                    <div>
                      <label className="label-eyebrow text-muted-foreground">
                        Fine policy notes <span className="text-red-500">*</span>
                      </label>
                      <textarea rows={3} className="input-base mt-2" placeholder="Explain your rules — e.g. Uniform must be all-black, reporting time is strict…"
                        value={fineNotes} onChange={e => setFineNotes(e.target.value)} />
                      <p className="text-[11px] text-muted-foreground mt-1">Shown to workers on this job listing before they apply.</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="border border-border rounded-xl p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
              <input type="checkbox" checked={travelExpenseEnabled} onChange={e => setTravelExpenseEnabled(e.target.checked)} className="w-4 h-4" />
              Enable travel allowance for this job
            </label>

            {travelExpenseEnabled && (
              <div className="mt-3 space-y-4">
                {!hasOfficeLocation && (
                  <p className="text-xs text-amber-500">
                    ⚠ Set your office location in your profile for accurate travel allowance calculations.
                  </p>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={useCustomTravelExpense} onChange={e => setUseCustomTravelExpense(e.target.checked)} className="w-4 h-4" />
                  Use a custom rate for this job
                </label>

                {!useCustomTravelExpense && (
                  <p className="text-xs text-muted-foreground">
                    {hasTravelDefault
                      ? "Your default rate per km (set in your profile) will apply to this job."
                      : "You haven't set a default rate yet. Set it in your profile, or check \"Use a custom rate for this job\" to define one here."}
                  </p>
                )}

                {useCustomTravelExpense && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">₹</span>
                      <input type="number" min="0" className="input-base !py-2 text-sm max-w-[8rem]" placeholder="per km"
                        value={travelExpenseRate} onChange={e => setTravelExpenseRate(e.target.value)} />
                      <span className="text-xs text-muted-foreground">per km</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Min.</span>
                      <input type="number" min="0" className="input-base !py-2 text-sm max-w-[8rem]" placeholder="e.g. 3"
                        value={travelExpenseMinKm} onChange={e => setTravelExpenseMinKm(e.target.value)} />
                      <span className="text-xs text-muted-foreground">km to qualify</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Max ₹</span>
                      <input type="number" min="0" className="input-base !py-2 text-sm max-w-[8rem]" placeholder="optional"
                        value={travelExpenseMaxAmount} onChange={e => setTravelExpenseMaxAmount(e.target.value)} />
                      <span className="text-xs text-muted-foreground">cap per worker (optional)</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card-flat bg-muted/30 text-sm text-muted-foreground">
            <p>📅 Date and venue inherited from event — not needed here</p>
          </div>

          <button disabled={loading} className="btn-primary w-full">
            {loading ? "Adding…" : "Add role to event"}
          </button>
        </form>
      )}

      {/* ── Fine intro popup ── */}
      {fineIntroOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setFineIntroOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">Rating categories & fines</h3>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              This lets you set optional rules to fine workers for issues like uniform or punctuality — deducted from their payment.
            </p>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              Completely optional. Workers always see your fine policy before applying — no hidden fines.
            </p>
            <button onClick={() => setFineIntroOpen(false)} className="btn-primary w-full">Got it</button>
          </div>
        </div>
      )}

      {/* Link existing job */}
      {tab === "link" && (
        <div className="mt-6 space-y-4">
          {myJobs.length === 0 ? (
            <div className="card-flat text-center py-8 text-muted-foreground">
              <p>No unlinked event staffing jobs found.</p>
              <p className="text-sm mt-1">Create a new role using the tab above.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Select a job to link to this event:</p>
              <div className="space-y-2">
                {myJobs.map(j => (
                  <button key={j.id} type="button" onClick={() => { setSelectedJob(j); setUpdateJobDate(null); }}
                    className={`w-full text-left card-flat transition ${selectedJob?.id === j.id ? "border-brand bg-brand-tint/30 ring-2 ring-brand shadow-md -translate-y-0.5" : "hover:border-brand"}`}>
                    <p className="font-medium text-foreground">{j.title}</p>
                    <p className="text-xs text-muted-foreground">{j.specialization} · ₹{j.payment} · {j.total_slots} slots</p>
                    {j.event_date && <p className="text-xs text-muted-foreground">Date: {new Date(j.event_date).toLocaleDateString("en-IN")}</p>}
                  </button>
                ))}
              </div>

              {/* Ask about date/venue update */}
              {selectedJob && updateJobDate === null && (
                <div className="card-flat border-brand/40 bg-brand-tint/20">
                  <p className="text-sm font-medium text-foreground mb-3">
                    Update this job's date and venue to match the event?
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Event: {event.date && new Date(event.date).toLocaleDateString("en-IN")} · {event.venue}
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setUpdateJobDate(true)} className="btn-primary flex-1 text-sm">Yes, update</button>
                    <button onClick={() => setUpdateJobDate(false)} className="btn-outline flex-1 text-sm">Keep original</button>
                  </div>
                </div>
              )}

              {selectedJob && updateJobDate !== null && (
                <button onClick={linkJob} disabled={loading} className="btn-primary w-full">
                  {loading ? "Linking…" : `Link "${selectedJob.title}" to this event`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
